import { Box } from "@chakra-ui/react";
import { colors, ColorIcon, customEvent, Spacing } from "goi_common";
import { BaseStack } from "goi_common/src/design-system";
import { useRef, useState, useCallback } from "react";
import Slider from "react-slick";
import { MubinsoCtaButton } from "./MubinsoUI";
import { LANDING_B_REVIEWS, LANDING_B_VIDEO } from "./mubinsoLandingBData";

/**
 * 무빈소 퍼널 B안 랜딩 화면.
 * 네이버 SA 특정 소재 유입 시 기존 A안 대신 노출.
 * 후기 캐러셀 + 장례지도사 영상 썸네일 + CTA.
 */
export default function MubinsoLandingB({ onNext }: { onNext: () => void }) {
  const [currentSlide, setCurrentSlide] = useState(0);
  const sliderRef = useRef<Slider | null>(null);
  const [videoPlaying, setVideoPlaying] = useState(false);

  const handlePlayVideo = useCallback(() => {
    customEvent({
      newGtm: { path: "mubinso_funnel", trigger_and_target: "landingB-video" },
    });
    setVideoPlaying(true);
  }, []);

  return (
    <BaseStack width="100%" maxW="480px" m="0 auto" minH="100dvh" px={24}>
      <Spacing gap={56} />

      {/* 타이틀 */}
      <Box width="100%" whiteSpace="pre-line" className="head2_b black">
        {"무빈소 장례를\n준비하고 계신가요?"}
      </Box>
      <Spacing gap={12} />
      <Box width="100%" whiteSpace="pre-line" className="body_16_r gray600">
        {"무빈소 장례만 2,000건을 함께한 고이가\n무엇부터 하면 되는지 알려드릴게요."}
      </Box>

      <Spacing gap={32} />

      {/* 후기 캐러셀 카드 */}
      <BaseStack width="100%" bg={colors.warmGray100} borderRadius="16px" p={20} spacing={16}>
        <Box className="subtitle_16_b gray900">실제 무빈소 진행 가족분들의 후기</Box>

        {/* 슬라이더 + 화살표(absolute로 카드 좌우 가장자리에 겹침) */}
        <Box width="100%" position="relative" px="10px">
          <Slider
            ref={sliderRef}
            dots={false}
            arrows={false}
            infinite
            autoplay
            autoplaySpeed={2500}
            slidesToShow={1}
            slidesToScroll={1}
            swipe
            beforeChange={(_, next) => {
              setCurrentSlide(next);
              customEvent({
                newGtm: { path: "mubinso_funnel", trigger_and_target: "landingB-review" },
              });
            }}
          >
            {LANDING_B_REVIEWS.map((review, i) => (
              <Box key={i} px="2px">
                <BaseStack
                  width="100%"
                  bg={colors.white}
                  borderRadius="12px"
                  p={20}
                  spacing={16}
                  minH="120px"
                >
                  <Box
                    className="body_16_r gray800"
                    lineHeight="1.6"
                    sx={{ "& b": { fontWeight: 700, color: colors.gray900 } }}
                    dangerouslySetInnerHTML={{ __html: `"${review.html}"` }}
                  />
                  <Box className="caption_12_r gray400">
                    {review.author} 고객님 - {review.date} 작성 후기
                  </Box>
                </BaseStack>
              </Box>
            ))}
          </Slider>
          <CarouselArrowButton
            direction="left"
            onClick={() => sliderRef.current?.slickPrev()}
            style={{
              position: "absolute",
              top: "50%",
              left: "-18px",
              transform: "translateY(-50%) rotate(180deg)",
            }}
          />
          <CarouselArrowButton
            direction="right"
            onClick={() => sliderRef.current?.slickNext()}
            style={{
              position: "absolute",
              top: "50%",
              right: "-18px",
              transform: "translateY(-50%)",
            }}
          />
        </Box>

        {/* dot 인디케이터 */}
        <BaseStack
          direction="horizontal"
          width="100%"
          alignItems="center"
          justifyContent="center"
          spacing={6}
        >
          {LANDING_B_REVIEWS.map((_, i) => (
            <Box
              key={i}
              width="8px"
              height="8px"
              borderRadius="50%"
              bg={i === currentSlide ? colors.gray900 : colors.gray200}
              transition="background 0.2s"
            />
          ))}
        </BaseStack>
      </BaseStack>

      <Spacing gap={24} />

      {/* 영상 */}
      <BaseStack width="100%" spacing={10}>
        <Box
          width="100%"
          position="relative"
          borderRadius="12px"
          overflow="hidden"
          style={{ aspectRatio: "16/9" }}
        >
          {videoPlaying ? (
            <Box
              as="iframe"
              width="100%"
              height="100%"
              position="absolute"
              top={0}
              left={0}
              src={`https://www.youtube.com/embed/${LANDING_B_VIDEO.youtubeId}?autoplay=1&rel=0`}
              title={LANDING_B_VIDEO.caption}
              frameBorder="0"
              allow="autoplay; encrypted-media"
              allowFullScreen
            />
          ) : (
            <Box
              width="100%"
              height="100%"
              position="absolute"
              top={0}
              left={0}
              cursor="pointer"
              onClick={handlePlayVideo}
            >
              <Box
                as="img"
                width="100%"
                height="100%"
                display="block"
                src={`https://img.youtube.com/vi/${LANDING_B_VIDEO.youtubeId}/mqdefault.jpg`}
                alt={LANDING_B_VIDEO.caption}
                style={{ objectFit: "cover" }}
              />
              {/* 재생 아이콘 오버레이 */}
              <Box
                position="absolute"
                top="50%"
                left="50%"
                transform="translate(-50%, -50%)"
                width="56px"
                height="56px"
                borderRadius="50%"
                bg="rgba(0,0,0,0.55)"
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <PlayIcon />
              </Box>
              {/* 우하단 재생시간 배지 */}
              <Box
                position="absolute"
                bottom="8px"
                right="8px"
                bg="rgba(0,0,0,0.7)"
                borderRadius="4px"
                px="6px"
                py="2px"
                className="caption_12_b"
                color={colors.white}
              >
                {LANDING_B_VIDEO.duration}
              </Box>
            </Box>
          )}
        </Box>
        <Box className="body_14_r gray500">{LANDING_B_VIDEO.caption}</Box>
      </BaseStack>

      <Spacing gap={40} />

      {/* CTA */}
      <Box width="100%">
        <MubinsoCtaButton label="무빈소 장례 알아보기" onClick={onNext} />
      </Box>
      <Spacing gap={40} />
    </BaseStack>
  );
}

// ---------------------------------------------------------------------------
// 내부 컴포넌트
// ---------------------------------------------------------------------------

function CarouselArrowButton({
  onClick,
  style,
}: {
  direction: "left" | "right";
  onClick: () => void;
  style?: React.CSSProperties;
}) {
  return (
    <Box
      as="button"
      width="28px"
      height="28px"
      borderRadius="50%"
      bg="transparent"
      display="flex"
      alignItems="center"
      justifyContent="center"
      cursor="pointer"
      onClick={onClick}
      flex="none"
      zIndex={1}
      style={style}
    >
      <ColorIcon name="arrow_right_small_v2" size={16} color={colors.gray900} />
    </Box>
  );
}

function PlayIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="white" aria-hidden>
      <path d="M8 5v14l11-7z" />
    </svg>
  );
}
